import { Link, useLocation } from "react-router-dom";
import {
  Home, Palette, Camera, Image, Brain, BookOpen, Clock, Crown,
  User, LogOut, Info, Shield, HeadphonesIcon, Sparkles, Globe,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import NotificationToggle from "@/components/NotificationToggle";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

export default function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();

  const mainLinks = [
    { path: "/", label: t("nav.home"), icon: Home },
    { path: "/face-chart", label: t("nav.faceChart"), icon: Palette },
    { path: "/analyzer", label: t("nav.analyzer"), icon: Camera },
    { path: "/gallery", label: t("nav.gallery"), icon: Image },
    { path: "/quiz", label: t("nav.quiz"), icon: Brain },
    { path: "/blog", label: t("nav.blog"), icon: BookOpen },
    { path: "/history", label: t("nav.history"), icon: Clock },
    { path: "/premium", label: t("nav.premium"), icon: Crown },
  ];

  const secondaryLinks = [
    { path: "/about", label: language === "es" ? "Sobre Nosotros" : "About Us", icon: Info },
    { path: "/privacy", label: language === "es" ? "Privacidad" : "Privacy", icon: Shield },
    { path: "/support", label: language === "es" ? "Soporte" : "Support", icon: HeadphonesIcon },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <Sidebar collapsible="icon" className="border-r border-border bg-sidebar">
      <SidebarContent className="py-4">
        {/* Logo */}
        <Link to="/" className={`px-4 mb-4 flex items-center gap-2 ${collapsed ? "justify-center" : ""} hover:opacity-80 transition-opacity`}>
          <Sparkles className="w-6 h-6 text-primary shrink-0" />
          {!collapsed && <span className="text-xl font-display font-bold text-gradient">glamcanva</span>}
        </Link>

        {/* Main nav */}
        <SidebarGroup>
          {!collapsed && <SidebarGroupLabel className="text-foreground/50 text-xs uppercase tracking-wider">{language === "es" ? "Herramientas" : "Tools"}</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {mainLinks.map(link => (
                <SidebarMenuItem key={link.path}>
                  <SidebarMenuButton asChild>
                    <Link
                      to={link.path}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isActive(link.path)
                          ? "bg-primary/20 text-accent font-bold"
                          : "text-foreground/70 hover:text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <link.icon className="w-5 h-5 shrink-0" />
                      {!collapsed && <span>{link.label}</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Secondary nav */}
        <SidebarGroup>
          {!collapsed && <SidebarGroupLabel className="text-foreground/50 text-xs uppercase tracking-wider mt-4">{language === "es" ? "Información" : "Info"}</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {secondaryLinks.map(link => (
                <SidebarMenuItem key={link.path}>
                  <SidebarMenuButton asChild>
                    <Link
                      to={link.path}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isActive(link.path)
                          ? "bg-primary/20 text-accent font-bold"
                          : "text-foreground/70 hover:text-foreground hover:bg-muted/50"
                      }`}
                    >
                      <link.icon className="w-5 h-5 shrink-0" />
                      {!collapsed && <span>{link.label}</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Account & Settings at bottom */}
        <div className="mt-auto px-3 space-y-2">
          {/* Profile */}
          <Link
            to="/profile"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              isActive("/profile")
                ? "bg-primary/20 text-accent font-bold"
                : "text-foreground/70 hover:text-foreground hover:bg-muted/50"
            }`}
          >
            <User className="w-5 h-5 shrink-0" />
            {!collapsed && <span>{t("nav.profile")}</span>}
          </Link>

          {/* Language toggle */}
          <div className={`flex items-center ${collapsed ? "justify-center" : "px-3"} gap-1`}>
            <Globe className="w-4 h-4 text-foreground/50 shrink-0" />
            {!collapsed && (
              <div className="flex gap-0.5 p-0.5 rounded-lg bg-muted/30 ml-1">
                <button
                  onClick={() => setLanguage("es")}
                  className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                    language === "es" ? "bg-primary text-primary-foreground" : "text-foreground/60 hover:text-foreground"
                  }`}
                >ES</button>
                <button
                  onClick={() => setLanguage("en")}
                  className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                    language === "en" ? "bg-primary text-primary-foreground" : "text-foreground/60 hover:text-foreground"
                  }`}
                >EN</button>
              </div>
            )}
          </div>

          {!collapsed && <NotificationToggle />}

          {/* Auth */}
          {user ? (
            <button
              onClick={signOut}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-muted/50 transition-all"
            >
              <LogOut className="w-5 h-5 shrink-0" />
              {!collapsed && <span>{t("nav.signOut")}</span>}
            </button>
          ) : (
            <Link
              to="/auth"
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold glow-fuchsia hover:brightness-110 transition-all"
            >
              {!collapsed ? t("nav.signIn") : <User className="w-5 h-5" />}
            </Link>
          )}
        </div>
      </SidebarContent>
    </Sidebar>
  );
}
